import { Device, i18n, waitAFrame } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Sprite } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import LocalisedText from '../ui/LocalisedText';
import { StatsWidget } from '../ui/StatsWidget';
import { SwipeScroller } from '../ui/SwipeScroller';
import { PaintMeta } from './../game/systems/ShopSystem';
import { NineSliceButton } from './../ui/buttons/NineSliceButton';
import { PaintIcon } from './../ui/PaintIcon';
import { PaintSelector } from './../ui/PaintSelector';
import { PurchasePanel } from './../ui/PurchasePanel';
import { RadioSelectorStyles } from './../ui/RadioSelector';
import { ShopPanel } from './../ui/ShopPanel';
import { RideStudioBaseShopScreen, RideStudioBaseShopScreenOptions } from './RideStudioBaseShopScreen';
export class RideStudioBodyShopScreen extends RideStudioBaseShopScreen
{
    protected options: RideStudioBaseShopScreenOptions;
    protected selectedText!: LocalisedText;
    protected tickIcon!: Sprite;
    private _paintSelector!: PaintSelector;
    private _stats: StatsWidget;
    private _currentPartID: string;
    private _startPartID: string;
    private _selectContainer!: Sprite;
    private _paintPurchaseContainer!: Sprite;
    // private _paintPurchaseBtn?: PurchasePanel; // Seems not being used anymore?

    private _currentChassisColor: string;
    private _currentActiveColorIndex: number;
    
    private _startChassisColor: string;
    private _startActiveColorIndex: number;

    private _leftSwipeScroller!: SwipeScroller;
    
    constructor(app: App, options: RideStudioBaseShopScreenOptions)
    {
        super(app, options);

        this.type = 'body';
        this._currentActiveColorIndex = 0;
        this._startActiveColorIndex = 0;
        this.options = options;

        // resize and re-order icons on settings change for consistency across app
        Shared.settings.shopIconSizeChanged.connect((newSize) =>
        {
            if (!this._paintSelector) return;

            this._paintSelector.radioSelector.resizeButtons(newSize);
            gsap.killTweensOf(this._paintSelector.view);
            this._paintSelector.view.y = 0;
        });

        this._stats = new StatsWidget();
        this.view.addChild(this._stats.view);

        this._currentPartID = '';

        this._currentChassisColor = '';
        
        this._startPartID = '';

        this._startChassisColor = '';
    }

    public show(): void
    {
        super.show();
        
        this.app.analytics.trackState(AnalyticsState.BODY);
        
        !Settings.spineDebug && this._stats.open();
        this.selectedText.visible = !Settings.spineDebug;
        this.tickIcon.visible = !Settings.spineDebug;
        this._selectContainer.visible = !Settings.spineDebug;
        this._paintPurchaseContainer.visible = !Settings.spineDebug;
        this._currentChassisColor = Shared.game.garage.selectedColorChassis;

        this._paintSelector.radioSelector.setActive(Shared.game.garage.colors.indexOf(this._currentChassisColor));
        this._currentActiveColorIndex = this._paintSelector.radioSelector.getActiveIndex();
        this.leftPanel.titleText.setText('rideStudioBodyShopScreen_type');
        this.rightPanel.titleText.setText('rideStudioBodyShopScreen_paint');
        this._currentPartID = Shared.game.garage.selectedChassis;
        
        this._startPartID = this._currentPartID;
        this._startChassisColor = this._currentChassisColor;
        this._startActiveColorIndex = this._currentActiveColorIndex;

        this.displayItemUnlockedStatus();
        this._leftSwipeScroller.refreshContent();
        this._paintSelector.snapToSelected(false);
        this.updateChassisIcon();
        
    }

    public hide(): void
    {
        this._stats.close();
    }

    public update(delta = 1): void
    {
        this._paintSelector.update(delta);
    }
    
    protected buildLeftPanel(): void
    {
        this.leftPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [],
            omitArrows: Device.mobile,
        });
        this.view.addChild(this.leftPanel.view);

        const garage = Shared.game.garage;

        this._leftSwipeScroller = this.createSwipeScroller();
        this.leftPanel.content.addChild(this._leftSwipeScroller.view);

        // connect buttons
        this.leftPanel.signals.onArrowUp.connect(() =>
        {
            //this.displayItemUnlockedStatus();
            this._leftSwipeScroller.nextItem();
            
            this.selectedText.visible = false;
            this.tickIcon.visible = false;
        });
        this.leftPanel.signals.onArrowDown.connect(() =>
        {
            
            //this.displayItemUnlockedStatus();
            this._leftSwipeScroller.prevItem();
            
            this.selectedText.visible = false;
            this.tickIcon.visible = false;
        });

        this.selectedText = new LocalisedText('rideStudioBodyShopScreen_selectedText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26,
            maxWidth: 250,
        });

        this.selectedText.anchor.set(0.5);
        this.selectedText.y = 65;
        this.leftPanel.view.addChild(this.selectedText);
        
        this.tickIcon = Sprite.from(Shared.assets.getTexture('equipped-tick'));
        this.tickIcon.scale.set(1.25);
        this.tickIcon.anchor.set(0.5);
        this.tickIcon.position.set(-60, 30);
        this.leftPanel.view.addChild(this.tickIcon);

        this._selectContainer = new Sprite();
        this._selectContainer.anchor.set(0.5);
        this._selectContainer.y = 280;
        this.leftPanel.addChild(this._selectContainer);
        this.displayItemUnlockedStatus();
    }

    protected buildRightPanel(): void
    {
        this.rightPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [],
            omitArrows: Device.mobile,
        });
        this.view.addChild(this.rightPanel.view);

        const padding = 6;

        this._paintSelector = new PaintSelector({
            buttonType: PaintIcon as any,
            style: RadioSelectorStyles.inlineWrap,
            padding,
            inlineWrapLimit: 300,
            buttons: this._createPaintButtonMeta(),
            containerHeight: 290,
            width: 274,
            height: 454,
            cancelCallback: () => this._cancelPaintPurchase(),
        });

        this._paintSelector.radioSelector.setSelected(this._paintSelector.radioSelector.getActiveIndex());

        Shared.game.shop.signals.colorUnlocked.connect((colorId) =>
        {
            // eslint-disable-next-line max-len
            (this._paintSelector.radioSelector._buttons[Shared.game.garage.colors.indexOf(colorId)] as any as PaintIcon).unlocked = true;
        });

        this.rightPanel.content.addChild(this._paintSelector.view);

        this._paintPurchaseContainer = new Sprite();
        this._paintPurchaseContainer.anchor.set(0.5);
        this._paintPurchaseContainer.y = 280;
        this.rightPanel.addChild(this._paintPurchaseContainer);

        this.rightPanel.signals.onArrowDown.connect(() =>
        {
            this._paintSelector.moveUp();
        });
        this.rightPanel.signals.onArrowUp.connect(() =>
        {
            this._paintSelector.moveDown();
        });
    }

    protected displayItemUnlockedStatus(): void
    {
        if (Settings.spineDebug) return;

        this._selectContainer.removeChildren();
        const data = this.app.data.getChassisData(Shared.game.garage.selectedChassis);
        const unlocked = Shared.game.shop.checkPurchased(Shared.game.garage.selectedChassis, 'bike');

        this.selectedText.visible = false;
        this.tickIcon.visible = false;
        
        if (unlocked)
        {
            this.selectedText.visible = true;
            this.tickIcon.visible = true;
            
            this._currentPartID = Shared.game.garage.selectedChassis;
            
        }
        else
        {
            const shopSystem = Shared.game.shop;
            const purchase = new PurchasePanel({
                enableCoinPurchase: true,
                enableDiamondPurchase: true,
                enableCancel: false,
                unlockCoinCost: data.coinPrice ?? 100,
                unlockDiamondCost: data.diamondPrice ?? 5,
            });

            this.selectedText.visible = false;
            this.tickIcon.visible = false;

            purchase.signals.coinBtnClick.connect((cost) =>
            {
                if (shopSystem.checkCoinsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message_generic',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        plainSprite: Shared.game.garage.getPlainSprite({
                            chassis: Shared.game.garage.selectedChassis,
                            color: this._currentChassisColor,
                        }),
                        coinCost: cost,
                        onConfirm: () => this._purchaseChassis(cost),
                    });
                }
            });

            purchase.signals.diamondBtnClick.connect((cost) =>
            {
                if (shopSystem.checkDiamondsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message_generic',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        plainSprite: Shared.game.garage.getPlainSprite(
                            {
                                chassis: Shared.game.garage.selectedChassis,
                                color: this._currentChassisColor,
                            }),
                        diamondCost: cost,
                        onConfirm: () => this._purchaseChassis(0, cost),
                    });
                }
            });

            this._selectContainer.addChild(purchase.view);
        }
        
        this.checkEnableConfirmButton();
    }

    protected updateChassisIcon(): void
    {
        const garage = Shared.game.garage;

        this._leftSwipeScroller.setCurrentIndex(garage.chassis.indexOf(garage.selectedChassis));
    }
    
    private _purchaseChassis(coins?: number, diamonds?: number): void
    {
        const shopSystem = Shared.game.shop;
        shopSystem.purchaseItem(Shared.game.garage.selectedChassis, 'bike', coins, diamonds);
        this.app.analytics.trackEvent(AnalyticsEvent.CONTENT_UNLOCKED, { game_item: `bike|${Shared.game.garage.selectedChassis}`});
        
        this._currentPartID = Shared.game.garage.selectedChassis;
        this.displayItemUnlockedStatus();
        this._selectContainer.removeChildren();
        this._leftSwipeScroller.refreshContent();
        
        if(shopSystem.checkAllTypeUnlocked("bike")){
            this.app.analytics.trackEvent(AnalyticsEvent.PURCHASED_ALL_ITEMS, { game_item: `bike|${Shared.game.garage.selectedChassis}`, content_type: 'bike'});
        }
        
    }
    
    private _createPaintButtonMeta(): any[]
    {
        const paintButtons: any[] = [];
        const shopSystem = Shared.game.shop;
        const paintMeta = shopSystem.getPaintMeta();
        const garage = Shared.game.garage;

        paintMeta.forEach((meta) =>
        {
            paintButtons.push({
                ...meta,
                onClick: () =>
                {
                    this._paintSelector.radioSelector.setActive(meta.index);
                    
                    Shared.game.shop.setColorChassis(meta.id);
                    
                    this._currentChassisColor = meta.id;
                    
                    if (Shared.game.shop.checkPurchased(meta.id, 'color'))
                    {
                        
                        this._currentActiveColorIndex = this._paintSelector.radioSelector.getActiveIndex();
                        this._paintSelector.radioSelector.setSelected(meta.index);
                    }
                    else{
                        this._paintSelector.radioSelector.setSelected(-1);
                    }
                    
                    this.checkEnableConfirmButton();

                    this._createPurchasePanel(meta);
                    this._leftSwipeScroller.refreshContent();
                },
            });
        });

        return paintButtons;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this._stats.view.x = this.app.stageCenter.x + 55;
        this._stats.view.y = this.app.stageBottom - 300;
    }

    // ==== SWIPE SCROLLER ========================================================================

    private createSwipeScroller(): SwipeScroller
    {
        const scroller = new SwipeScroller({
            length: Shared.game.garage.chassis.length,
            offsetY: -10,
            width: 274,
            height: 454,
            spacing: 150,
        }, this.leftSwipeScrollerContentProvider.bind(this));

        scroller.onSnap.connect(this.swipeScrollerSnapHandler.bind(this));
        scroller.onStartDrag.connect(this.swipeScrollerStartDragHandler.bind(this));

        return scroller;
    }

    /**
     * Provide sprites to a swipe scroller
     * @param index - Reference index for the content
     * @returns Sprite to be used inside the scroller
     */
    private leftSwipeScrollerContentProvider(index: number): Container
    {
        const garage = Shared.game.garage;
        const chassis = garage.getPlainSprite({ chassis: garage.chassis[index], color: garage.selectedColor });

        const unlocked = Shared.game.shop.checkPurchased(garage.chassis[index], 'bike');
        const selected = garage.chassis[index] === this._currentPartID;

        chassis.scale.set(190 / chassis.width);

        if (!selected && !unlocked)
        {
            const lock = Sprite.from(Shared.assets.getTexture('icon-locked'));

            lock.scale.set(0.7 / chassis.scale.x);
            lock.anchor.set(0.5);
            lock.position.set(-60 / chassis.scale.x, 30 / chassis.scale.x);

            chassis.addChild(lock);
        }

        return chassis;
    }

    /** Fires when scroller snaps to one of its items */
    private swipeScrollerSnapHandler(scroller: SwipeScroller): void
    {
        const index = scroller.getCurrentIndex();

        Shared.game.garage.setChassis(Shared.game.garage.chassis[index]);
        this.displayItemUnlockedStatus();
    }

    /** Fires when user start dragging the scroller */
    private swipeScrollerStartDragHandler(): void
    {
        this.selectedText.visible = false;
        this.tickIcon.visible = false;
        this._selectContainer.removeChildren();
    }

    private _createPurchasePanel(meta: PaintMeta): void
    {
        this._paintPurchaseContainer.removeChildren();
        const unlocked = Shared.game.shop.checkPurchased(meta.id, 'color');

        if (unlocked)
        {
            return;
        }

        const shopSystem = Shared.game.shop;
        const purchase = new PurchasePanel({
            enableCoinPurchase: !!meta.coinCost,
            enableDiamondPurchase: !!meta.diamondCost,
            unlockCoinCost: meta.coinCost,
            unlockDiamondCost: meta.diamondCost,
            enableCancel: false,
        });

        // this._paintPurchaseBtn = purchase;

        purchase.signals.cancelBtnClick.connect(() => this._cancelPaintPurchase());

        purchase.signals.coinBtnClick.connect(() =>
        {
            if (shopSystem.checkCoinsAvailable(meta.coinCost))
            {
                this.app.popup.presentConfirmPurchasePopup({
                    title: 'purchasePopup_title',
                    message: 'purchasePopup_message_paint',
                    confirm: 'purchasePopup_confirm',
                    cancel: 'purchasePopup_cancel',
                    tint: meta.tint,
                    itemSprite: 'paint-tint-storeitem',
                    coinCost: meta.coinCost,
                    onConfirm: () => this._purchasePaint(meta.id, meta.index, meta.coinCost),
                });
            }
        });

        purchase.signals.diamondBtnClick.connect(() =>
        {
            if (shopSystem.checkDiamondsAvailable(meta.diamondCost))
            {
                this.app.popup.presentConfirmPurchasePopup({
                    title: 'purchasePopup_title',
                    message: 'purchasePopup_message_paint',
                    confirm: 'purchasePopup_confirm',
                    cancel: 'purchasePopup_cancel',
                    tint: meta.tint,
                    itemSprite: 'paint-tint-storeitem',
                    diamondCost: meta.diamondCost,
                    onConfirm: () => this._purchasePaint(meta.id, meta.index, 0, meta.diamondCost),
                });
            }
        });

        this._paintPurchaseContainer.addChild(purchase.view);
    }
    
    private _purchasePaint(id: string, index: number, coins?: number, diamonds?: number): void
    {
        this.app.analytics.trackEvent(AnalyticsEvent.CONTENT_UNLOCKED, { game_item: `color|${id}`});
        
        const shopSystem = Shared.game.shop;
        if(shopSystem.areAllPaintsUnlocked()){
            this.app.analytics.trackEvent(AnalyticsEvent.PURCHASED_ALL_ITEMS, { game_item: `color|${id}`, content_type: 'bike|paint'});
        }
        
        shopSystem.purchaseItem(id, 'color', coins, diamonds);
        // eslint-disable-next-line max-len
        const icon = this._paintSelector.radioSelector._buttons[Shared.game.garage.colors.indexOf(id)] as any as PaintIcon;

        icon.unlocked = true;

        this._currentChassisColor = id;

        this._currentActiveColorIndex = this._paintSelector.radioSelector.getActiveIndex();
        this._paintSelector.radioSelector.setSelected(index);
        this._paintSelector.radioSelector.setActive(index);
        this._paintPurchaseContainer.removeChildren();
        Shared.game.shop.signals.colorUnlocked.emit(id);
        Shared.game.soundSystem.playSfx('paint-unlock');
        
        this.checkEnableConfirmButton();
    }

    private async _cancelPaintPurchase(): Promise<void>
    {
        const shopSystem = Shared.game.shop;

        // hack, some sort of race condition occurs here
        // waiting for a few frames works around the issue
        await waitAFrame(5);

        shopSystem.setColorChassis(this._currentChassisColor);

        this._leftSwipeScroller.refreshContent();
        this._paintSelector.radioSelector.setActive(this._currentActiveColorIndex);

        this._paintPurchaseContainer.removeChildren();
    }
    
    private checkEnableConfirmButton(){
        
        const unlockedChassis = Shared.game.shop.checkPurchased(Shared.game.garage.selectedChassis, 'bike');
        const unlockedColor = Shared.game.shop.checkPurchased(this._currentChassisColor, 'color');

        if(unlockedChassis && unlockedColor){
            this.confirmButton.enable();
        }
        else{
            this.confirmButton.disable();
        }
        
    }
    
    protected onCancelButtonPressed(): void
    {
        super.onCancelButtonPressed();
        
        this._paintSelector.radioSelector.setActive(this._startActiveColorIndex);
        this._paintSelector.radioSelector.setSelected(this._startActiveColorIndex);
        
        Shared.game.shop.setColorChassis(this._startChassisColor);
        Shared.game.shop.setChassis(this._startPartID);
    }
    
    protected onConfirmButtonPressed(): void
    {
        super.onConfirmButtonPressed();
        
        Shared.game.shop.setColorChassis(this._currentChassisColor);
        Shared.game.shop.setChassis(this._currentPartID);
        
        Shared.game.garage.saveVehicleSelection();
        
        //this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: `garage|body|${this._currentPartID}` });
        //this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: `garage|color|${garage.selectedChassis}|${meta.id}` });
    }
}
